import {
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  type MultiFactorError,
  type User as FirebaseUser,
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '@/firebase/config';
import { writeAuditLog } from '@shared/utils';
import type { LoginResult } from '../types';

const googleProvider = new GoogleAuthProvider();
const microsoftProvider = new OAuthProvider('microsoft.com');

function toJapaneseError(code: string): string {
  const map: Record<string, string> = {
    'auth/popup-closed-by-user': 'ログインがキャンセルされました。',
    'auth/popup-blocked': 'ポップアップがブロックされました。ブラウザの設定を確認してください。',
    'auth/account-exists-with-different-credential': '別の方法で登録済みのアカウントです。',
    'auth/cancelled-popup-request': 'ログイン操作がキャンセルされました。',
    'auth/network-request-failed': 'ネットワークエラーが発生しました。接続を確認してください。',
    'auth/too-many-requests': 'しばらく時間をおいてから再度お試しください。',
    'auth/user-disabled': 'このアカウントは無効化されています。管理者にお問い合わせください。',
    'auth/user-not-found': 'メールアドレスまたはパスワードが正しくありません。',
    'auth/wrong-password': 'メールアドレスまたはパスワードが正しくありません。',
    'auth/invalid-credential': 'メールアドレスまたはパスワードが正しくありません。',
    'auth/invalid-email': '有効なメールアドレスを入力してください。',
  };
  return map[code] ?? 'ログインに失敗しました。再度お試しください。';
}

export async function signInWithEmail(email: string, password: string): Promise<LoginResult> {
  try {
    await signInWithEmailAndPassword(auth, email, password);
    await notifySignIn();
    return { success: true };
  } catch (error: unknown) {
    const code = (error as { code?: string }).code ?? '';
    await notifyLoginFailed('email', code);
    return { success: false, error: toJapaneseError(code) };
  }
}

async function notifySignIn(): Promise<void> {
  try {
    const fn = httpsCallable(functions, 'recordSignIn');
    await fn({});
  } catch {
    // サインイン記録の失敗はユーザー体験に影響させない
  }
}

async function notifyLoginFailed(provider: string, errorCode: string): Promise<void> {
  try {
    const fn = httpsCallable(functions, 'recordLoginFailed');
    await fn({ provider, errorCode });
  } catch {
    // 記録失敗はユーザー体験に影響させない
  }
}

export async function signInWithGoogle(): Promise<LoginResult> {
  try {
    await signInWithPopup(auth, googleProvider);
    await notifySignIn();
    return { success: true };
  } catch (error: unknown) {
    const code = (error as { code?: string }).code ?? '';
    if (code === 'auth/multi-factor-auth-required') {
      return { success: false, mfaError: error as MultiFactorError };
    }
    await notifyLoginFailed('google', code);
    return { success: false, error: toJapaneseError(code) };
  }
}

export async function signInWithMicrosoft(): Promise<LoginResult> {
  try {
    await signInWithPopup(auth, microsoftProvider);
    await notifySignIn();
    return { success: true };
  } catch (error: unknown) {
    const code = (error as { code?: string }).code ?? '';
    if (code === 'auth/multi-factor-auth-required') {
      return { success: false, mfaError: error as MultiFactorError };
    }
    await notifyLoginFailed('microsoft', code);
    return { success: false, error: toJapaneseError(code) };
  }
}

export async function signOutUser(): Promise<void> {
  try {
    const currentUser = auth.currentUser;
    if (currentUser) {
      // ログアウト前に audit 記録（認証済み状態で Firestore に直接書き込む）
      try {
        await writeAuditLog({
          entityType: 'user',
          entityId: currentUser.uid,
          action: 'update',
          userId: currentUser.uid,
          after: { event: 'logout' },
        });
      } catch {
        // audit 記録の失敗はログアウト処理に影響させない
      }
    }
    await signOut(auth);
  } catch (error: unknown) {
    const msg = error instanceof Error ? error.message : String(error);
    throw new Error(`ログアウトに失敗しました: ${msg}`);
  }
}

export function subscribeToAuthState(
  callback: (user: FirebaseUser | null) => void
): () => void {
  return onAuthStateChanged(auth, callback);
}
